<script setup async>
import {competitionFacts} from '~/utils/privateAccess';
const {outputDateFromDb} = useCrudConverters();
import {computed} from "vue";
import {createError} from 'nuxt/app';
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import {useStatsPage} from '~/composables/useStatsPage';
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import RequestAlert from "~/components/clubs/RequestAlert.vue";
import StatsView from "~/components/stats/StatsView.vue";
import ShortLinkShareWrapper from "~/components/share/ShortLinkShareWrapper.vue";
import ArchivedNotice from "~/components/ArchivedNotice.vue";

definePageMeta({
  name: 'LeagueCycleStats',
  path: '/leagues/:leagueId/cycles/:cycleId/stats',
});

const route = useRoute();
const router = useRouter();
const leagueId = route.params.leagueId;
const cycleId = route.params.cycleId;

// Fetch league to get cycle and league information
const {item: league, getItem: getLeague, hasItemErrors: hasLeagueErrors} = useCrudApi("api/v2/leagues");
await getLeague(leagueId);

if (hasLeagueErrors.value) {
  throw createError({statusCode: 404, message: 'Box league not found'});
}

const selectedCycle = computed(() => league.value?.cycles?.find(c => c.id === cycleId));

if (!selectedCycle.value) {
  throw createError({statusCode: 404, message: 'Box league cycle not found'});
}

const {stats} = await useStatsPage(
    'leagueCycle',
    cycleId,
    'api/v2/leagueCycles',
    {leagueCycleId: cycleId},
    null,
);

const {setMeta} = useHeadService();
const cycleTitle = selectedCycle.value.name || `Cycle ${selectedCycle.value.number || ''}`;
setMeta(
    `${league.value.name} · ${cycleTitle} Stats on Tap Leagues`,
    `View detailed stats for ${league.value.name} · ${cycleTitle} on Tap Leagues.`
);

const {isAdminOfClub} = useAuthService();
const isAdmin = computed(() => isAdminOfClub(league.value?.clubId));

// The league or the cycle has been deleted (archived) - show the deleted notice instead.
const leagueArchived = computed(() => !!league.value?.archivedAt);
const leaguePrivate = computed(() => !!league.value?.privateToViewer);
usePrivatePage(leaguePrivate);
const cycleArchived = computed(() => !!selectedCycle.value?.archivedAt);

const titleActions = computed(() => isAdmin.value
    ? [{
      title: 'Edit box league',
      icon: 'mdi-pencil',
      click: () => router.push({name: 'ClubLeagueSettings', params: {clubId: league.value.clubId, leagueId: league.value.id}}),
      color: 'info',
    }]
    : []);
</script>

<template>
  <base-page>
    <!-- Deleted (archived) league/cycle - show the notice instead of any content -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): the stub keeps the name and the join link -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): identity only, then the notice where the content would be -->
    <v-container v-if="leaguePrivate" class="large">
      <frontend-header :league="league"
          :club="league.club ?? (league.clubId ? {id: league.clubId, slug: league.clubId, name: league.clubName} : null)"
          :title="`${league.name} - Stats`"
          title-icon="mdi-table"
          
      >
        <template #subTitle>
          <club-chip v-if="league.club" :club="league.club"></club-chip>
          <span v-else-if="league.clubName" class="tl-chip">{{ league.clubName }}</span>
        </template>
      </frontend-header>
      <div class="tl-action-strip tl-action-strip--solo my-5">
        <short-link-share-wrapper>
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>
      </div>
      <private-notice reason="competition" entity="box league" :name="league.name" :club-name="league.clubName" :join-slug="league.visibleInviteLink?.slug"
                      :facts="competitionFacts('league', league, d => outputDateFromDb(d, 'd MMM yyyy'))"/>
    </v-container>
    <v-container v-else-if="leagueArchived" class="large">
      <archived-notice entity="box league" :name="league.name"/>
    </v-container>
    <v-container v-else-if="cycleArchived" class="large">
      <archived-notice entity="cycle" :name="selectedCycle.name"/>
    </v-container>
    <template v-else>
    <v-container class="large">
      <frontend-header
          :club="league.club"
          :league="league"
          title-icon="mdi-table"
          :title="`${league.name} · ${selectedCycle.name || 'Cycle ' + (selectedCycle.number || '')} · Stats`"
          :actions="titleActions"
      >
        <template #subTitle>
          <p class="fh-sub mb-0">Cycle statistics</p>
        </template>
      </frontend-header>

      <div class="tl-action-strip mt-4">
        <short-link-share-wrapper>
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>

        <router-link class="as" :to="{ name: 'LeagueDetail', params: { leagueId, cycleId } }">
          <v-icon size="17" icon="mdi-table"/>Box league
        </router-link>

        <router-link class="as" :to="{ name: 'FixtureSearch', query: { clubIds: [league.clubId], leagueIds: [leagueId], leagueCycleIds: [cycleId] } }">
          <v-icon size="17" icon="mdi-scoreboard"/>Matches
        </router-link>
      </div>
    </v-container>

    <v-container class="large" v-if="stats && stats.length">
      <request-alert class="mb-6" :id="cycleId" namespace="leagueCycles"/>

      <stats-view :stats="stats" form-mode="allTime"/>
    </v-container>
    </template>
  </base-page>
</template>

<style scoped lang="scss">
.fh-sub {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  line-height: 1.6;
}
</style>
